import { z } from 'zod'
import type { TareaForm } from './useTareaForm'

/**
 * Regla de repetición de una tarea (ORR-304).
 *
 * El vocabulario es el mismo que el de la tabla `tareas_recurrentes` y el de
 * `calcular_proxima_fecha_tarea`: la fecha la calcula siempre la base, nunca el
 * front. Acá solo se elige la regla y se la describe en castellano.
 */

export const FRECUENCIAS = [
  { value: 'semanal', label: 'Cada semana' },
  { value: 'mensual', label: 'Cada mes' },
  { value: 'bimestral', label: 'Cada 2 meses' },
  { value: 'trimestral', label: 'Cada 3 meses' },
  { value: 'semestral', label: 'Cada 6 meses' },
  { value: 'anual', label: 'Una vez al año' },
] as const

/**
 * Modos que ofrece el desplegable. No incluye 'primer_habil': lo cubre 'primer_dia' con el
 * corrimiento al día hábil siguiente. La base y el cron siguen aceptando 'primer_habil'.
 */
export const DIA_MODOS = [
  { value: 'dia_fijo', label: 'Un día del mes' },
  { value: 'primer_dia', label: 'El primer día del mes' },
  { value: 'ultimo_dia', label: 'El último día del mes' },
  { value: 'ultimo_habil', label: 'El último día hábil del mes' },
] as const

const PRIMER_HABIL = { value: 'primer_habil', label: 'El primer día hábil del mes' } as const

/**
 * Opciones del desplegable para el modo guardado. Una repetición guardada con 'primer_habil'
 * suma su opción, porque un Select cuyo valor no está entre las opciones se muestra vacío.
 */
export const opcionesDiaModo = (actual: DiaModo) =>
  actual === 'primer_habil' ? [...DIA_MODOS, PRIMER_HABIL] : DIA_MODOS

export const DIAS_SEMANA = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
] as const

export type Frecuencia = (typeof FRECUENCIAS)[number]['value']
export type DiaModo = ReglaRecurrencia['dia_modo']

export const recurrenciaSchema = z.object({
  frecuencia: z.enum(['semanal', 'mensual', 'bimestral', 'trimestral', 'semestral', 'anual']),
  dia_modo: z.enum(['dia_fijo', 'primer_dia', 'ultimo_dia', 'primer_habil', 'ultimo_habil']),
  dia_del_mes: z.number().int().min(1).max(31).nullable().optional(),
  dia_semana: z.number().int().min(1).max(7).nullable().optional(),
  correr_a_habil: z.boolean(),
})

export type ReglaRecurrencia = z.infer<typeof recurrenciaSchema>

export const REGLA_POR_DEFECTO: ReglaRecurrencia = {
  frecuencia: 'mensual',
  dia_modo: 'dia_fijo',
  dia_del_mes: 1,
  dia_semana: null,
  correr_a_habil: true,
}

/** Día del mes de una fecha 'YYYY-MM-DD'. Devuelve null sin fecha. */
export const diaDelMesDeFecha = (fecha: string | null | undefined): number | null => {
  const dia = Number(fecha?.slice(8, 10))
  return dia >= 1 && dia <= 31 ? dia : null
}

/**
 * Copia el día de la fecha límite al "Día del mes" de la repetición. No pisa un día que el usuario
 * escribió: su tipeo marca el campo como `isDirty`, y esta escritura no lo marca.
 */
export const autocompletarDiaDelMes = (form: TareaForm, fecha: string | null | undefined) => {
  const dia = diaDelMesDeFecha(fecha)
  if (dia == null) return
  if (form.getFieldMeta('rec_dia_del_mes')?.isDirty) return
  form.setFieldValue('rec_dia_del_mes', dia, { dontUpdateMeta: true })
}

/**
 * El corrimiento al día hábil siguiente solo tiene sentido en los modos que
 * pueden caer en un día no laborable. En 'ultimo_dia' correr hacia adelante
 * cruzaría al mes siguiente, y los modos hábiles ya devuelven un día hábil.
 * Misma regla que aplica `calcular_proxima_fecha_tarea`.
 */
export const admiteCorrerAHabil = (diaModo: DiaModo): boolean =>
  diaModo === 'dia_fijo' || diaModo === 'primer_dia'

const CADA: Record<Frecuencia, string> = {
  semanal: 'cada semana',
  mensual: 'de cada mes',
  bimestral: 'cada 2 meses',
  trimestral: 'cada 3 meses',
  semestral: 'cada 6 meses',
  anual: 'una vez al año',
}

/**
 * Traduce la regla a la línea que lee el usuario antes de guardar. Es lo único
 * que le dice cuándo va a caer la tarea: sin esto, "último día hábil, cada 3
 * meses" se guarda a ciegas.
 */
export const describirRecurrencia = (regla: ReglaRecurrencia): string => {
  const { frecuencia, dia_modo, dia_del_mes, dia_semana, correr_a_habil } = regla

  if (frecuencia === 'semanal') {
    const dia = DIAS_SEMANA.find((d) => d.value === dia_semana)?.label
    if (!dia) return 'Elegí el día de la semana.'
    const base = `Todos los ${dia.toLowerCase()}`
    return correr_a_habil
      ? `${base}. Si cae feriado, pasa al día hábil siguiente.`
      : `${base}.`
  }

  const cada = CADA[frecuencia]
  let base: string

  switch (dia_modo) {
    case 'dia_fijo':
      if (!dia_del_mes) return 'Elegí el día del mes.'
      base =
        frecuencia === 'mensual'
          ? `El ${dia_del_mes} de cada mes`
          : `El día ${dia_del_mes}, ${cada}`
      break
    case 'primer_dia':
      base = frecuencia === 'mensual' ? 'El primer día de cada mes' : `El primer día del mes, ${cada}`
      break
    case 'ultimo_dia':
      base = frecuencia === 'mensual' ? 'El último día de cada mes' : `El último día del mes, ${cada}`
      break
    case 'primer_habil':
      base =
        frecuencia === 'mensual'
          ? 'El primer día hábil de cada mes'
          : `El primer día hábil del mes, ${cada}`
      break
    case 'ultimo_habil':
      base =
        frecuencia === 'mensual'
          ? 'El último día hábil de cada mes'
          : `El último día hábil del mes, ${cada}`
      break
  }

  if (dia_del_mes && dia_del_mes > 28 && dia_modo === 'dia_fijo') {
    base += `. En los meses más cortos, el último día`
  }

  if (correr_a_habil && admiteCorrerAHabil(dia_modo)) {
    return `${base}. Si cae feriado o fin de semana, el día hábil siguiente.`
  }
  return `${base}.`
}
